import type { Plan } from '@/types';

type SelectSuggestedUpgradePlansParams = {
  plans?: Plan[] | null;
  currentPlanId?: string | null;
  onFallback?: (details: {
    reason: 'current_plan_not_found' | 'target_positions_missing';
    availablePaidPlans: number;
    currentIndex: number;
    targetIndices: number[];
  }) => void;
};

const getPlanPrice = (plan: Plan) =>
  plan.monthly_price?.price ?? plan.yearly_price?.price ?? 0;

export const selectSuggestedUpgradePlans = ({
  plans,
  currentPlanId,
  onFallback,
}: SelectSuggestedUpgradePlansParams): Plan[] => {
  const paidPlans = (plans ?? [])
    .filter((plan) => plan.monthly_price || plan.yearly_price)
    .sort((a, b) => getPlanPrice(a) - getPlanPrice(b));

  if (paidPlans.length === 0) {
    return [];
  }

  let currentIndex = -1;
  let targetIndices = [1, 3];

  if (currentPlanId) {
    currentIndex = paidPlans.findIndex((plan) => plan.id === currentPlanId);
    if (currentIndex === -1) {
      onFallback?.({
        reason: 'current_plan_not_found',
        availablePaidPlans: paidPlans.length,
        currentIndex,
        targetIndices,
      });
    } else {
      targetIndices = [currentIndex + 2, currentIndex + 4];
    }
  }

  const selectedPlans: Plan[] = [];
  const selectedPlanIds = new Set<string>();

  targetIndices.forEach((index) => {
    const candidate = paidPlans[index];
    if (!candidate || selectedPlanIds.has(candidate.id)) return;
    if (currentIndex >= 0 && index <= currentIndex) return;
    selectedPlanIds.add(candidate.id);
    selectedPlans.push(candidate);
  });

  if (selectedPlans.length < 2) {
    onFallback?.({
      reason: 'target_positions_missing',
      availablePaidPlans: paidPlans.length,
      currentIndex,
      targetIndices,
    });

    paidPlans.forEach((candidate, index) => {
      if (selectedPlans.length >= 2) return;
      if (selectedPlanIds.has(candidate.id)) return;
      if (currentIndex >= 0 && index <= currentIndex) return;
      selectedPlanIds.add(candidate.id);
      selectedPlans.push(candidate);
    });
  }

  return selectedPlans.slice(0, 2);
};
